import { Row, Shape, ShapeStream } from '@electric-sql/client';
import { setLoadingState } from '../../redux/db/actions';
import store from '../../redux/store';

type LoadingStateKey =
	| 'user'
	| 'sceneIds'
	| 'scenes'
	| 'activeScene.frames'
	| 'activeScene.chats'
	| 'guest.scenes'
	| 'guest.frames';

// tell the client to include cookies when it makes a request
function fetchClient(input: RequestInfo | URL, init?: RequestInit) {
	return fetch(input, {
		credentials: 'include',
		...init,
	});
}

export abstract class Stream<T extends Row<unknown>> {
	protected aborter: AbortController | null = null;
	protected stream: ShapeStream<T> | null = null;
	protected shape: Shape<T> | null = null;

	constructor(
		protected url: string,
		// Params are required for all streams to ensure proper Electric SQL client behavior.
		//
		// They serve multiple purposes:
		// 1. Cache busting - prevents browser from returning cached data when users change
		// 2. Electric SQL client stability - prevents offset=-1 and other parameter issues
		// 3. Handle mismatch prevention - in our system, often the server decides what
		//    data to stream (by, for example, using the userId to get the list of desired sceneIds,
		//    and then doing a "where" on those sceneIds). This goes against the base use case of electric
		//    which is "params passes a list of sceneIds that are directly dumped into the where clause"
		//    For some of our uses (pass in a userId, electric returns the entry from the user table)
		//    everything is fine. But for other uses (pass in a userId, electric returns the list of sceneIds
		//    the user has, and then does a "where" on those sceneIds), we must ensure the handle is distinct.
		//    Since we dont' know the data the server will return, often we pass in a random shapeBust parameter
		//    *** IMPORTANT: With electric, handles must be 1:1 with shape definitions, and when the
		//        server-side shape changes but the client-side handle parameters don't, you get an error
		//        "shape definition and handle do not match".
		protected params: any,
		protected onError?: (error: Error) => void,
		protected loadingResource?: LoadingStateKey
	) {}

	start() {
		this.stop('cleanup'); // Clean up any existing stream

		// Set loading state if resource specified
		if (this.loadingResource) {
			store.dispatch(setLoadingState({ resource: this.loadingResource, state: 'loading' }));
		}

		this.aborter = new AbortController();
		this.stream = new ShapeStream<T>({
			url: this.url,
			signal: this.aborter.signal,
			params: this.params,
			fetchClient: fetchClient,
			onError: this.onError,
		});

		this.shape = new Shape(this.stream);

		// Subscribe to data changes
		this.shape.subscribe((data) => {
			this.onShapeData(data.rows);
			// Automatically mark as loaded after data is processed
			if (this.loadingResource) {
				store.dispatch(setLoadingState({ resource: this.loadingResource, state: 'loaded' }));
			}
		});

		// Subscribe to stream messages (currently used for optimistic updates)
		this.stream.subscribe((messages: Array<unknown>) => {
			this.onStreamMessages(messages);
		});
	}

	stop(reason: string = 'Stream stopped') {
		this.aborter?.abort(reason);
		this.aborter = null;
		this.stream = null;
		this.shape = null;
	}

	// Abstract methods for subclasses to implement
	abstract onShapeData(rows: T[]): void;
	abstract onStreamMessages(messages: Array<unknown>): void;
}
